import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Trash2, Pencil, Loader2, ExternalLink, Upload, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DatasheetsManager = ({ open, onOpenChange, products = [] }) => {
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchDatasheets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/datasheets');
      setDatasheets(res.data);
    } catch { toast.error('Kon datasheets niet laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchDatasheets(); }, [open]);

  const handleUpload = async (fileList) => {
    const filesArr = Array.from(fileList || []);
    if (!filesArr.length) return;
    setUploading(true);
    try {
      for (const f of filesArr) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post('/datasheets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${filesArr.length} datasheet(s) geüpload`);
      fetchDatasheets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload mislukt'); }
    finally { setUploading(false); }
  };

  const saveRename = async (ds) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/datasheets/${ds.id}`, { name: editName.trim() });
      toast.success('Naam aangepast');
      setEditingId(null);
      fetchDatasheets();
    } catch { toast.error('Kon naam niet aanpassen'); }
  };

  const linkProduct = async (ds, productId) => {
    try {
      await api.put(`/datasheets/${ds.id}`, { product_id: productId === 'none' ? '' : productId });
      toast.success('Productkoppeling bijgewerkt');
      fetchDatasheets();
    } catch { toast.error('Kon koppeling niet opslaan'); }
  };

  const remove = async (ds) => {
    try {
      await api.delete(`/datasheets/${ds.id}`);
      toast.success('Datasheet verwijderd');
      fetchDatasheets();
    } catch { toast.error('Kon niet verwijderen'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Datasheets bibliotheek</DialogTitle>
          <DialogDescription>
            Upload datasheets, pas namen aan en koppel ze aan producten. Gekoppelde datasheets gaan automatisch mee naar de klantmap bij "Naar Sharepoint sturen".
          </DialogDescription>
        </DialogHeader>

        <div className="border border-dashed rounded-xl p-4 flex items-center gap-3">
          <Upload className="w-5 h-5 text-muted-foreground" />
          <div className="flex-1">
            <Label>Nieuwe datasheet(s) uploaden (PDF)</Label>
            <Input type="file" multiple accept=".pdf,application/pdf" disabled={uploading}
              onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
              data-testid="datasheet-upload-input" />
          </div>
          {uploading && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : datasheets.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Nog geen datasheets geüpload.</p>
        ) : (
          <div className="space-y-2">
            {datasheets.map(ds => (
              <div key={ds.id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`datasheet-row-${ds.id}`}>
                <FileText className="w-5 h-5 text-red-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  {editingId === ds.id ? (
                    <div className="flex items-center gap-2">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveRename(ds)}
                        className="h-8" data-testid={`datasheet-rename-input-${ds.id}`} />
                      <Button size="icon" className="h-8 w-8" onClick={() => saveRename(ds)} data-testid={`datasheet-rename-save-${ds.id}`}><Check className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-sm truncate">{ds.name}</p>
                      <p className="text-xs text-muted-foreground">{fmtSize(ds.size)} · {ds.uploaded_by_name}</p>
                    </>
                  )}
                </div>
                <Select value={ds.product_id || 'none'} onValueChange={(v) => linkProduct(ds, v)}>
                  <SelectTrigger className="w-[200px] h-8 text-xs" data-testid={`datasheet-product-select-${ds.id}`}>
                    <SelectValue placeholder="Koppel product..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Geen product —</SelectItem>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Bekijken"
                  onClick={() => window.open(`${api.defaults.baseURL}/datasheets/${ds.id}/file`, '_blank')}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Naam aanpassen"
                  onClick={() => { setEditingId(ds.id); setEditName(ds.name); }} data-testid={`datasheet-rename-${ds.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Verwijderen"
                  onClick={() => remove(ds)} data-testid={`datasheet-delete-${ds.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
